import { Card } from '../../../molecules/Card/Card';
import {
  isEmpty,
  type ChartBaseProps,
  type ChartRow,
} from '../../../molecules/ChartFrame/chartProps';
import { legendItems, type ChartSeries } from '../../../molecules/ChartFrame/chartTheme';
import { ChartLegend } from '../../../molecules/ChartLegend/ChartLegend';
import { KPI, type KPIMetric } from '../../../molecules/KPI/KPI';
import { BarChart } from '../../../organisms/BarChart/BarChart';
import { PLOT_HEIGHT, cardState, spanClass, type WidgetBlockProps } from '../shared/cardShell';

export type SentimentTrendView = {
  /** `Net sentiment` and `Positive share`. */
  kpis: KPIMetric[];
  /** One row per week (or month), oldest first; one key per sentiment (counts). */
  rows: ChartRow[];
  /** The axis label: `Wk 1`, `Wk 2` (or the month). */
  xKey: string;
  /** Where each row keeps its dates — `Sep 7–13` — which head its tooltip. */
  rangeKey: string;
  /** The sentiments present, in option order — stacked from the axis up. */
  series: ChartSeries[];
  /** Whether any mention is in scope. */
  hasRows: boolean;
  formatValue: (value: number) => string;
  /** Comparison-period reading per row and sentiment; nothing when comparison is off. */
  formatCompare?: ChartBaseProps['formatCompare'];
};

export type SentimentTrendCardProps = WidgetBlockProps & {
  view?: SentimentTrendView;
  /** Opens the conversations behind a column (index into `rows`). */
  onViewDetails?: (index: number) => void;
};

/**
 * Sentiment trend over time — the sentiment mix of each week as a normalised
 * column (GP's `Executive Overview` mock, 2026-10-28). Not in Figma: `BarChart`'s
 * normalised stack standing upright, which three sentiments over a handful of
 * weeks reads well as (docs/design/rules.md §2, share over time). Each share
 * and its count are in the tooltip, against the comparison period's.
 */
export function SentimentTrendCard({
  status,
  view,
  span,
  chrome,
  onRetry,
  onViewDetails,
}: SentimentTrendCardProps) {
  const hasData = view !== undefined && view.hasRows && !isEmpty(view.rows, view.series);
  return (
    <Card
      /* Set only when this card is a widget on a custom report — see `CardChrome`. */
      {...chrome}
      className={spanClass(span)}
      {...cardState(status, hasData, onRetry)}
      title="Sentiment trend over time"
      helpText="Our sentiment mix week by week: how much of each week's conversation was positive, neutral and negative."
      helpSupportingText="Net sentiment is the positive share minus the negative share, in points. Weeks run Monday to Sunday and are counted from the period's first, so the first and last can be part-weeks; each column's tooltip gives its dates. Past 70 days the columns are the source's own weeks or months."
      kpi={view && <KPI metrics={view.kpis} />}
      chart={
        view && (
          <BarChart
            data={view.rows}
            onViewDetails={onViewDetails}
            xKey={view.xKey}
            series={view.series}
            stacked
            normalize
            tooltipLabelKey={view.rangeKey}
            height={PLOT_HEIGHT}
            legend="none"
            formatValue={view.formatValue}
            formatCompare={view.formatCompare}
          />
        )
      }
      legend={view && <ChartLegend items={legendItems(view.series)} />}
    />
  );
}
